import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { BlogListingFiltersProps, BlogTopicData } from 'models/blog-types';
import styles from './BlogListing.module.scss';
import classNames from 'classnames/bind';
import Icon from 'components/helpers/Icon';
import { useDictionary } from 'lib/utils';
import { getUniqueSortedTopics, useTopicFilter } from '../Functions';
import Button from 'components/helpers/Button/Button';

const BlogListingFilters: React.FC<BlogListingFiltersProps> = (props) => {
  const router = useRouter();
  const clearFiltersLabel = useDictionary('Clear Filters');
  const filtersLabel = useDictionary('Filters');
  const byTopicLabel = useDictionary('By Topic');
  const viewMore = useDictionary('View More');
  const viewLess = useDictionary('View Less');

  const updateTopicsInUrl = useTopicFilter(router);
  const cx = classNames.bind(styles);

  const [selectedTopics, setSelectedTopics] = useState<BlogTopicData[]>([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [filtersActive, setFiltersActive] = useState(false);

  const { posts, filtersToShowOnLoad, onFilterChangeCallback } = props;
  const topics = getUniqueSortedTopics(posts);

  const [filtersToShow, setFiltersToShow] = useState(filtersToShowOnLoad);
  const [viewAllTopics, setViewAllTopics] = useState(false);

  const showViewMore = topics.length > filtersToShowOnLoad;

  const onViewMoreClick = () => {
    const topicsToShow = !viewAllTopics ? topics.length : filtersToShowOnLoad;
    setViewAllTopics(!viewAllTopics);
    setFiltersToShow(topicsToShow);
  };

  const updateFilters = (topics: BlogTopicData[]) => {
    setSelectedTopics(topics);
    onFilterChangeCallback(topics);
    updateTopicsInUrl(topics);
  };

  const onClearFilters = () => {
    updateFilters([]);
  };

  useEffect(() => {
    if (filtersLoaded) {
      return;
    }
    setFiltersLoaded(true);
    const queryParam = router.query.topics as string | undefined;

    if (!queryParam || !Array.isArray(topics)) {
      return;
    }

    const urlIds = queryParam.split(',').map((id) => id.trim());
    const matchedTopics = topics.filter((topic) => urlIds.includes(topic.id));

    setSelectedTopics(matchedTopics);
    onFilterChangeCallback(matchedTopics);
  }, [
    router.query.topics,
    topics,
    filtersLoaded,
    onFilterChangeCallback,
    setSelectedTopics,
    setFiltersLoaded,
    filtersToShow,
  ]);

  const onTopicCheckboxClick = (topic: BlogTopicData) => {
    let $currentTopics = selectedTopics;
    const $exists = selectedTopics.find((t) => t.id === topic.id);
    $currentTopics = $exists ? $currentTopics.filter((t) => t.id !== topic.id) : [...$currentTopics, topic];

    updateFilters($currentTopics);
  };

  return (
    <div className={cx('filters', `filters__active--${filtersActive}`)}>
      <Button className={cx('filters--filter-heading-md')} onClick={() => setFiltersActive(!filtersActive)}>
        {filtersLabel}
        <Icon variant={filtersActive ? 'close' : 'filter'} />
      </Button>

      <h3 className={cx('filters--filter-heading-lg')}>{filtersLabel}</h3>

      <div className={cx('filters__list')}>
        <h4>{byTopicLabel}</h4>

        {topics.slice(0, filtersToShow).map((topic: BlogTopicData, index: number) => (
          <label key={`topic-filter-${index}`}>
            <input
              type="checkbox"
              onChange={() => {
                onTopicCheckboxClick(topic);
              }}
              checked={selectedTopics.some((t) => t.id === topic.id)}
              id={topic.id}
            />
            {topic.topic || topic.name}
          </label>
        ))}

        {showViewMore && (
          <a onClick={onViewMoreClick} className={cx('view-more-topics')}>
            {viewAllTopics ? viewLess : viewMore}
            <Icon variant={viewAllTopics ? 'minus' : 'plus'} />
          </a>
        )}

        {selectedTopics.length > 0 && (
          <div className={cx('clear-filters')}>
            <a href="javascript:" onClick={onClearFilters}>
              {clearFiltersLabel} <Icon variant="close" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogListingFilters;
